import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchOptionType } from '../api';
import { IOnlineTourSearchPropertyResult } from '../utils/types';
import { DateRangeInput } from './DateRangeInput';
import { FromAutoCompleteDialogContainer } from '../container/FromAutoCompleteDialogContainer';

export function SearchForm() {
  const [selectedSearchTypeOption, setSelectedSearchOption] =
    useState<IOnlineTourSearchPropertyResult | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const nav = useNavigate();
  const [fromSearchOpen, setFromSearchOpen] = useState(false);

  const handleSearchButtonClick = () => {
    const sp = new URLSearchParams();

    if (selectedSearchTypeOption) {
      switch (selectedSearchTypeOption.type) {
        case SearchOptionType.CATEGORY:
          sp.append('categories', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.TOUR_DAY_COUNT:
          sp.append('tourDayCount', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.TOUR_CONCEPT:
          sp.append('tourConcept', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.DEPARTURE_CITIES:
          sp.append('departureCities', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.DEPARTURE_AIRPORTS:
          sp.append(
            'departureAirports',
            selectedSearchTypeOption.id.toString(),
          );
          break;
        case SearchOptionType.ACC_CITIES:
          sp.append('accCities', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.TOUR_PRODUCT_TYPES:
          sp.append('tourProductTypes', selectedSearchTypeOption.id.toString());
          break;
      }
    }

    if (startDate) {
      sp.append('startDate', format(startDate, 'yyyy-MM-dd'));
    }
    if (endDate) {
      sp.append('endDate', format(endDate, 'yyyy-MM-dd'));
    }

    if (Array.from(sp).length > 0) {
      nav(`/tours?${sp.toString()}`);
    } else {
      nav(`/tours`);
    }
  };

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1}
      sx={{
        my: 2,
      }}
    >
      <Stack
        flex={1}
        direction={isMobile ? 'column' : 'row'}
        spacing={1}
        alignItems={isMobile ? 'stretch' : 'center'}
        sx={{
          bgcolor: 'white',
          borderRadius: 1,
          px: 2,
          pb: 2,
          pt: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            [theme.breakpoints.down('md')]: {
              pb: 2,
            },
          }}
        >
          <TextField
            onClick={() => setFromSearchOpen(true)}
            value={selectedSearchTypeOption?.searchText || ''}
            label={t('searchTour.searchPlaceholder')}
            fullWidth
            variant="standard"
            InputLabelProps={{
              shrink: Boolean(selectedSearchTypeOption?.searchText),
            }}
          />
          {fromSearchOpen && (
            <FromAutoCompleteDialogContainer
              value={null}
              onChange={setSelectedSearchOption}
              onClose={() => setFromSearchOpen(false)}
              open
            />
          )}
        </Box>
        <DateRangeInput
          value={[startDate || undefined, endDate || undefined]}
          onChange={(val) => {
            if (val[0]) setStartDate(val[0]);
            if (val[1]) setEndDate(val[1]);
          }}
        />
      </Stack>
      <Button variant="contained" onClick={handleSearchButtonClick}>
        <SearchIcon />
        {t('searchTour.searchBtn')}
      </Button>
    </Stack>
  );
}
